// Device tracking utilities for app statistics
import api from './api';

// Generate or retrieve device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = 'device_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

// Detect platform
const getPlatform = () => {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return 'ios';
  }
  if (/Android/.test(ua)) {
    return 'android';
  }
  return 'web';
};

// Check if PWA
const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

// Get device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let deviceModel = 'Unknown';
  let deviceManufacturer = 'Unknown';
  let osVersion = 'Unknown';
  
  // iOS detection
  if (/iPad/.test(ua)) {
    deviceModel = 'iPad';
    deviceManufacturer = 'Apple';
    const match = ua.match(/OS (\d+[_\.]\d+)/);
    if (match) osVersion = match[1].replace('_', '.');
  } else if (/iPhone/.test(ua)) {
    deviceModel = 'iPhone';
    deviceManufacturer = 'Apple';
    const match = ua.match(/OS (\d+[_\.]\d+)/);
    if (match) osVersion = match[1].replace('_', '.');
  } else if (/Android/.test(ua)) {
    const match = ua.match(/Android (\d+\.?\d*)/);
    if (match) osVersion = match[1];
    
    // Try to get device model
    const modelMatch = ua.match(/;\s*([^;]+)\s*Build/);
    if (modelMatch) {
      deviceModel = modelMatch[1].trim();
      // Common manufacturers
      const manufacturers = ['Samsung', 'Huawei', 'Xiaomi', 'OPPO', 'Vivo', 'OnePlus', 'Realme', 'Google', 'LG', 'Sony', 'Motorola'];
      for (const mfr of manufacturers) {
        if (deviceModel.toLowerCase().includes(mfr.toLowerCase())) {
          deviceManufacturer = mfr;
          break;
        }
      }
    }
  } else {
    // Desktop/Web
    if (/Windows/.test(ua)) {
      deviceManufacturer = 'Microsoft';
      deviceModel = 'Windows PC';
      const match = ua.match(/Windows NT (\d+\.?\d*)/);
      if (match) osVersion = match[1];
    } else if (/Mac/.test(ua)) {
      deviceManufacturer = 'Apple';
      deviceModel = 'Mac';
      const match = ua.match(/Mac OS X (\d+[_\.]\d+)/);
      if (match) osVersion = match[1].replace('_', '.');
    } else if (/Linux/.test(ua)) {
      deviceModel = 'Linux PC';
    }
  }
  
  return { deviceModel, deviceManufacturer, osVersion };
};

// App version
const APP_VERSION = '2.0.0';

// Track device registration
export const registerDevice = async () => {
  try {
    const deviceId = getDeviceId();
    const platform = getPlatform();
    const { deviceModel, deviceManufacturer, osVersion } = getDeviceInfo();
    
    const data = {
      device_id: deviceId,
      platform: platform,
      os_version: osVersion,
      app_version: APP_VERSION,
      device_model: deviceModel,
      device_manufacturer: deviceManufacturer,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language || 'ar',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      is_pwa: isPWA()
    };
    
    await api.post('/app-stats/register-device', data);
    console.log('📱 Device registered:', platform, deviceModel);
    
    // Store last registered version
    localStorage.setItem('last_app_version', APP_VERSION);
    
    return true;
  } catch (error) {
    console.error('Failed to register device:', error);
    return false;
  }
};

// Track app update
export const trackAppUpdate = async (oldVersion, newVersion) => {
  try {
    const deviceId = getDeviceId();
    const platform = getPlatform();
    
    await api.post('/app-stats/track-update', {
      device_id: deviceId,
      platform: platform,
      old_version: oldVersion,
      new_version: newVersion,
      update_source: 'auto'
    });
    
    console.log('🔄 Update tracked:', oldVersion, '->', newVersion);
    localStorage.setItem('last_app_version', newVersion);
    
    return true;
  } catch (error) {
    console.error('Failed to track update:', error);
    return false;
  }
};

// Track app activity
export const trackActivity = async (eventType, sessionDuration = null) => {
  try {
    const deviceId = getDeviceId();
    
    await api.post('/app-stats/track-activity', {
      device_id: deviceId,
      event_type: eventType,
      session_duration: sessionDuration
    });
    
    return true;
  } catch (error) {
    console.error('Failed to track activity:', error);
    return false;
  }
};

// Check if update occurred
export const checkForVersionChange = () => {
  const lastVersion = localStorage.getItem('last_app_version');
  if (lastVersion && lastVersion !== APP_VERSION) {
    trackAppUpdate(lastVersion, APP_VERSION);
    return true;
  }
  return false;
};

// Initialize device tracking
export const initDeviceTracking = () => {
  // Register device on first visit or track update
  const lastVersion = localStorage.getItem('last_app_version');
  
  if (!lastVersion) {
    // First time visitor
    registerDevice();
  } else if (lastVersion !== APP_VERSION) {
    // App was updated
    trackAppUpdate(lastVersion, APP_VERSION);
    registerDevice(); // Re-register with new version
  } else {
    // Same version, just track activity
    trackActivity('app_open');
  }
  
  // Track session duration on page unload
  const sessionStart = Date.now();
  window.addEventListener('beforeunload', () => {
    const duration = Math.round((Date.now() - sessionStart) / 1000);
    // Use sendBeacon with proper content type for reliability
    const deviceId = getDeviceId();
    const data = JSON.stringify({
      device_id: deviceId,
      event_type: 'app_close',
      session_duration: duration
    });
    // Create Blob with proper content type
    const blob = new Blob([data], { type: 'application/json' });
    navigator.sendBeacon('/api/app-stats/track-activity', blob);
  });
};

export default {
  registerDevice,
  trackAppUpdate,
  trackActivity,
  initDeviceTracking,
  getDeviceId,
  getPlatform,
  isPWA
};
